import axios from "axios";
import { FindestButton, TopbarNotification } from "Components/Shared";
import { GeneralConstants } from "Constants";
import { ToastTypeEnum } from "Enums";
import { AxiosHelperSingleton, CookieHelperSingleton, ToastHelperSingleton } from "Helpers";
import { FC, useContext, useState } from "react";
import { CaseContext, AuthContext } from "Stores";
import Modal from "react-modal";
import modalStyles from "styles/modal.module.scss";
import styles from "./dynamicDeliveryTrial.module.scss";

export const DynamicDeliveryTrial: FC = () => {
    // Context
    const { caseState } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);
    // Constants
    const COOKIE_NAME = `HideDynamicDeliveryAd-${caseState.selectedCaseId}`;

    // State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isNotificationVisible, setIsNotificationVisible] = useState(true);
    const [isMoreInfoModalOpen, setIsMoreInfoModalOpen] = useState(false);

    // Check if the user already clicked a button
    const isTrialHidden = CookieHelperSingleton.doesCookieExist(COOKIE_NAME);

    // If the user already has hidden this notification or already has access to dynamic delivery or 
    // is on the SaaS environment then don't show the notification
    if(isTrialHidden || caseState.isDynamicDelivery || !caseState.selectedCase?.isPhaseOnePublished || authState.igorAIEnv.trim().toLowerCase() === GeneralConstants.IGORAI_SAAS_ENV.trim().toLowerCase()) {
        return null;
    }


    const hideDynamicDeliveryAd = (numberOfDays: number) => {
        CookieHelperSingleton.setCookie(COOKIE_NAME, "true", numberOfDays);
    };

    const onYesClick = async () => {
        const formData = new FormData();
        formData.append("dynamicDeliveryFreeTrialDaysNumber", `${authState.dynamicDeliveryFreeTrialDaysNumber}`);
        const result = await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/RequestDynamicDelivery/${caseState.selectedCaseId}`,
            formData);
        if(result.status !== 200) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Could not request free trial.");
            return;
        }

        setIsMoreInfoModalOpen(false);
        setIsModalOpen(true);
        setIsNotificationVisible(false);
        hideDynamicDeliveryAd(365);
    };

    const onNoClick = async () => {
        hideDynamicDeliveryAd(365);
        setIsNotificationVisible(false);
        const formData = new FormData();
        formData.append("isRemindLater", "false");
        formData.append("dynamicDeliveryFreeTrialDaysNumber", `${authState.dynamicDeliveryFreeTrialDaysNumber}`);
        await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/RefuseDynamicDelivery/${caseState.selectedCaseId}`,
            formData);
    };

    const onOtherButtonClick = async () => {
        hideDynamicDeliveryAd(7);
        setIsNotificationVisible(false);
        const formData = new FormData();
        formData.append("isRemindLater", "true");
        formData.append("dynamicDeliveryFreeTrialDaysNumber", `${authState.dynamicDeliveryFreeTrialDaysNumber}`);
        await axios.post(`${AxiosHelperSingleton.getServerBaseURL()}Cases/RefuseDynamicDelivery/${caseState.selectedCaseId}`,
            formData);
    };

    const onMoreInfoClick = () => {
        setIsMoreInfoModalOpen(true);
    };

    return (
        <>
            <Modal 
                className={`${modalStyles.modal} ${modalStyles.center} ${styles.modal}`} 
                overlayClassName={modalStyles.overlay}
                isOpen={isModalOpen} 
                onRequestClose={() => { setIsModalOpen(false); }}
            >
                <h2>Free trial requested</h2>
                <span>Thank you for signing up! Findest will contact you shortly to activate IGOR^AI.</span>
                <div style={{marginTop: "1rem"}}>
                    <FindestButton onClick={() => { setIsModalOpen(false); } } title="Close" />
                </div>
            </Modal>
            <Modal 
                className={`${modalStyles.modal} ${modalStyles.center} ${styles.moreInfoModal}`} 
                overlayClassName={modalStyles.overlay}
                isOpen={isMoreInfoModalOpen} 
                onRequestClose={() => { setIsMoreInfoModalOpen(false); }}
            >
                <div className={styles.headerContainer}>
                    <h1>Activate IGOR^AI on your case</h1>
                    <p><strong>Turn your technology scouting project into a dynamic report.</strong> Remain up to date with the state of the art, dive deeper into the scouting results and edit the case information to keep your whole team on the same page. Make use of the possibility to get IGOR^AI with your free trial.</p>
                    <div className={styles.actionButtons}>
                        <button type="button" onClick={onYesClick}>Activate my trial</button>
                        <a href="https://calendly.com/dewilde/igor-ai-dynamicdelivery?month=2022-06" target="_blank" rel="noopener noreferrer">Discover the posibilities</a>
                    </div>
                </div>
                <div className={styles.contentContainer}>
                    <div className={styles.contentMainTitle}>Explore the features of IGOR^AI</div>
                    <div className={styles.contentSubTitle}>Identify more documents and understand which organizations are active in a technology field</div>
                    <div className={[styles.contentSection, styles.left].join(" ")}>
                        <div className={styles.contentSectionTitle}>Document search</div>
                        <div className={styles.contentSectionText}><strong>Find more relevant publications</strong> and patents for technologies.</div>
                        <img src="/images/searchondelivery.gif" alt="" />
                    </div>
                    <div className={[styles.contentSection, styles.right].join(" ")}>
                        <div className={styles.contentSectionTitle}>Organization search</div>
                        <div className={styles.contentSectionText}>Use IGOR to <strong>gather company intelligence</strong> on who is publishing and patenting the technology. See publication trends and which companies are the most active.</div>
                        <img src="/images/organization-search.gif" alt="" />
                    </div>
                    <div className={styles.contentSubTitle}>Evaluate technologies faster with real-time question answering and search documents based on units mentioned</div>
                    <div className={[styles.contentSection, styles.left].join(" ")}>
                        <div className={styles.contentSectionTitle}>Question Answering</div>
                        <div className={styles.contentSectionText}>With <strong>real-time question answering</strong>, you can ask IGOR^AI questions within a technology result. IGOR^AI then uses the abstract data available in the technology to reason if the answer is known.</div>
                        <img src="/images/real-time-question-answering.gif" alt="" />
                    </div>
                    <div className={[styles.contentSection, styles.right].join(" ")}>
                        <div className={styles.contentSectionTitle}>Unit-based document search</div>
                        <div className={styles.contentSectionText}>Search for articles and patents that mention a unit of interest. You can must match, should match, or must not match these units in the query. It is even possible to select units that meet specific criteria such as larger than, smaller than, or between values!</div>
                        <img src="/images/unit-search-gif.gif" alt="" />
                    </div>
                    <div className={styles.contentSubTitle}>Search based on similar terms and see directly which paper is open access</div>
                    <div className={[styles.contentSection, styles.left].join(" ")}>
                        <div className={styles.contentSectionTitle}>Search with similar terms</div>
                        <div className={styles.contentSectionText}>Rapidly expand your search results with generated terms similar to the added keywords.</div>
                        <img src="/images/generated-synonyms-gif.gif" alt="" />
                    </div>
                    <div className={[styles.contentSection, styles.right].join(" ")}>
                        <div className={styles.contentSectionTitle}>Open access check</div>
                        <div className={styles.contentSectionText}>With the open-access feature, you can continue discovering information and your search flow is not broken by an article behind a paywall. You can see if the article is open access by the open "green" lock.</div>
                        <img src="/images/open-access-article-check.gif" alt="" />
                    </div>
                </div>
                <div className={styles.footerContainer}>
                    <div className={styles.actionButtons}>
                        <button type="button" onClick={onYesClick}>Activate my trial</button>
                        <a href="https://calendly.com/dewilde/igor-ai-dynamicdelivery?month=2022-06" target="_blank" rel="noopener noreferrer">Discover the posibilities</a>
                    </div>
                </div>
            </Modal>
            { isNotificationVisible ?
                <TopbarNotification message={`Try IGOR^AI in your case ${authState.dynamicDeliveryFreeTrialDaysNumber}-days for free`} onYesClick={onYesClick}
                    onNoClick={onNoClick} otherButtonText="Remind me later" onOtherButtonClick={onOtherButtonClick}
                    className={styles.dynamicDeliveryTrial} onMoreInfoClick={onMoreInfoClick} />
                : null }
        </>
    );
};